import { assert } from '@common/errors';
import { getPrivacyModeEnabled } from './localStorage';

const dollarsWithCentsFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const dollarsWithoutCentsFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

function randomIntFromInterval(min: number, max: number) {
    // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

// Return signed (relative to input) integer between 0 & 10k dollars, in cents.
function randomizeNumInCents(numInCents: number): number {
    return numInCents < 0
        ? randomIntFromInterval(0, -10_000_00)
        : randomIntFromInterval(0, 10_000_00);
}

function toDollars(
    numInCents: number,
    opts: {
        withCents: boolean;
        alwaysSigned?: boolean;
    } = {
        withCents: false,
        alwaysSigned: false,
    }
): string {
    assert(Number.isInteger(numInCents), 'Cents must be integer');

    // Ensure we never show "-$0" in the UI
    if (Object.is(numInCents, -0)) numInCents = 0;

    // HACK: To enable a cheap "privacy mode", we return a random value anywhere
    // this function is called if privacy mode is enabled.
    // NOTE: This relies on the assumption we're calling this helper everywhere
    // we show a currency value in the UI; if that changes, we'll need to
    // refine this approach.
    if (getPrivacyModeEnabled()) {
        numInCents = randomizeNumInCents(numInCents);
    }

    const num = numInCents / 100.0;

    const result = opts.withCents
        ? dollarsWithCentsFormatter.format(num)
        : dollarsWithoutCentsFormatter.format(num);

    if (opts.alwaysSigned) {
        return num > 0 ? `+${result}` : result;
    } else {
        return result;
    }
}

function toDollarsWithCents(numInCents: number): string {
    return toDollars(numInCents, { withCents: true });
}

function toDollarsShort(numInCents: number): string {
    const signed = (dollarStr: string): string => {
        const sign = numInCents < 0 ? '-' : '';
        return sign + dollarStr;
    };
    const absNumInCents = Math.abs(numInCents);

    // If less than $1k, return actual dollar amount without cents
    if (absNumInCents < 1000 * 100) return signed(toDollars(absNumInCents));

    // If less than $1m, round to nearest thousand and return thousands + "k"
    if (absNumInCents < 1000000 * 100) {
        const numInThousandCents = Math.round(absNumInCents / 1000);
        return signed(toDollars(numInThousandCents) + 'k');
    }

    // If $1m or greater, round to nearest million (floor) with hundred- &
    // ten-thousands place decimals, if non-zero, and return amount + "m"
    const numInMillionCents = Math.floor(absNumInCents / 100000000) * 100;

    // Show hundred+ten thousands places as decimals, if non-zero
    let amount = toDollars(numInMillionCents);
    const inTenThousands = Math.round(absNumInCents / 1000000) + '';
    const lastChar = inTenThousands.length - 1;
    const hundredThousands = inTenThousands.slice(lastChar - 1, lastChar);
    const tenThousands = inTenThousands.slice(lastChar);
    if (tenThousands !== '0') {
        amount += '.' + hundredThousands + tenThousands;
    } else if (hundredThousands !== '0') {
        amount += '.' + hundredThousands;
    }
    return signed(amount + 'm');
}

export { toDollars, toDollarsShort, toDollarsWithCents };
